import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Divider, 
  IconButton, 
  Tooltip, 
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Slider,
  Collapse,
  CircularProgress,
  useTheme,
  alpha
} from '@mui/material';
import { 
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  MoreVert as MoreVertIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  Info as InfoIcon,
  InfoOutlined as InfoOutlinedIcon,
  CenterFocusStrong as CenterFocusStrongIcon,
  FormatColorFill as FormatColorFillIcon
} from '@mui/icons-material';
import TimeSavedIndicator from '../TimeSavedIndicator';
import { MindMapNode } from '../../services/contentService';
import html2canvas from 'html2canvas';

interface MindMapProps {
  data: MindMapNode;
  timeSaved: number;
  videoTitle: string;
  isSaved?: boolean;
  onSave?: () => void;
  onShare?: () => void;
  onDownload?: () => void;
  onPrint?: () => void;
}

// Interface for D3 tree node format
interface D3TreeNode {
  name: string;
  attributes: {
    details: string;
    color?: string;
  };
  children: D3TreeNode[];
}

// Color palette for nodes
const NODE_COLORS = [
  '#00b4d8', // Primary blue
  '#0077b6', // Darker blue
  '#00a896', // Teal
  '#ef476f', // Pink
  '#ffd166', // Yellow
  '#06d6a0', // Green
  '#118ab2', // Blue
  '#073b4c', // Dark blue
  '#ff9e00', // Orange
  '#9b5de5'  // Purple
];

const MindMapEnhanced: React.FC<MindMapProps> = ({
  data,
  timeSaved,
  videoTitle,
  isSaved = false,
  onSave = () => {},
  onShare = () => {},
  onDownload = () => {},
  onPrint = () => {}
}) => {
  const theme = useTheme();
  
  // State for more options menu
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const moreMenuOpen = Boolean(moreAnchorEl);
  
  // State for zoom level
  const [zoomLevel, setZoomLevel] = useState<number>(1);
  
  // State for fullscreen
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  
  // State for selected node
  const [selectedNode, setSelectedNode] = useState<D3TreeNode | null>(null);
  
  // State for node info visibility
  const [showNodeInfo, setShowNodeInfo] = useState<boolean>(true);
  
  // State for react-d3-tree component
  const [Tree, setTree] = useState<any>(null);
  
  // State for tree data
  const [treeData, setTreeData] = useState<D3TreeNode | null>(null);
  
  // State for dimensions
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  
  // State for color mode
  const [colorMode, setColorMode] = useState<boolean>(true);
  
  // Ref for the container
  const containerRef = useRef<HTMLDivElement>(null);
  const treeContainerRef = useRef<HTMLDivElement>(null);
  
  // Dynamically import react-d3-tree to avoid the "exports is not defined" error
  useEffect(() => {
    const loadTree = async () => {
      try {
        // Using dynamic import
        const module = await import('react-d3-tree');
        setTree(() => module.default || module.Tree);
      } catch (error) {
        console.error('Error loading react-d3-tree:', error);
      }
    };
    
    loadTree();
  }, []);
  
  // Update dimensions when container size changes
  useEffect(() => {
    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(entries => {
        for (let entry of entries) {
          const { width, height } = entry.contentRect;
          setDimensions({ width, height });
        }
      });
      
      resizeObserver.observe(containerRef.current);
      
      // Initial dimensions
      setDimensions({
        width: containerRef.current.offsetWidth,
        height: containerRef.current.offsetHeight || 400
      });
      
      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);
  
  // Get node name (label or name property)
  const getNodeName = useCallback((node: MindMapNode): string => {
    // Check if node has label property (ExtendedMindMapNode)
    if ('label' in node && typeof node.label === 'string') {
      return node.label || node.name;
    }
    return node.name;
  }, []);
  
  // Get node details
  const getNodeDetails = useCallback((node: MindMapNode): string => {
    // Check if node has details property (ExtendedMindMapNode)
    if ('details' in node && typeof node.details === 'string') {
      return node.details;
    }
    // Try to use description or content if available
    if ('description' in node && typeof (node as any).description === 'string') {
      return (node as any).description;
    }
    if ('content' in node && typeof (node as any).content === 'string') {
      return (node as any).content;
    }
    return '';
  }, []);
  
  // Assign colors to nodes based on their level
  const assignNodeColors = useCallback((node: MindMapNode, level: number = 0): D3TreeNode => {
    const color = NODE_COLORS[level % NODE_COLORS.length];
    
    return {
      name: getNodeName(node),
      attributes: {
        details: getNodeDetails(node),
        color
      },
      children: node.children 
        ? node.children.map(child => assignNodeColors(child, level + 1)) 
        : []
    };
  }, [getNodeName, getNodeDetails]);
  
  // Convert MindMapNode to react-d3-tree format
  const convertToD3TreeFormat = useCallback((node: MindMapNode): D3TreeNode => {
    return assignNodeColors(node);
  }, [assignNodeColors]);
  
  // Fallback data if there's an error or no data
  const getFallbackData = (): D3TreeNode => {
    return {
      name: 'Main Topic',
      attributes: {
        details: '',
        color: NODE_COLORS[0]
      },
      children: [
        {
          name: 'Subtopic 1',
          attributes: {
            details: 'Details about subtopic 1',
            color: NODE_COLORS[1]
          },
          children: [
            { 
              name: 'Detail 1.1',
              attributes: {
                details: 'More information about detail 1.1',
                color: NODE_COLORS[2]
              },
              children: []
            },
            { 
              name: 'Detail 1.2',
              attributes: {
                details: 'More information about detail 1.2',
                color: NODE_COLORS[2]
              },
              children: []
            }
          ]
        },
        {
          name: 'Subtopic 2',
          attributes: {
            details: 'Details about subtopic 2',
            color: NODE_COLORS[1]
          },
          children: [
            { 
              name: 'Detail 2.1',
              attributes: {
                details: 'More information about detail 2.1',
                color: NODE_COLORS[2]
              },
              children: []
            },
            { 
              name: 'Detail 2.2',
              attributes: {
                details: 'More information about detail 2.2',
                color: NODE_COLORS[2]
              },
              children: []
            }
          ]
        }
      ]
    };
  };
  
  // Process data into tree format
  useEffect(() => {
    if (data) {
      try {
        // Convert data to D3 tree format
        const processedData = convertToD3TreeFormat(data);
        setTreeData(processedData);
      } catch (error) {
        console.error('Error processing mind map data:', error);
        setTreeData(getFallbackData());
      }
    } else {
      setTreeData(getFallbackData());
    }
  }, [data, convertToD3TreeFormat]);
  
  // Handle more menu open
  const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };
  
  // Handle more menu close
  const handleMenuClose = () => {
    setMoreAnchorEl(null);
  };
  
  // Handle save
  const handleSave = () => {
    onSave();
  };
  
  // Handle share
  const handleShare = () => {
    onShare();
    handleMenuClose();
  };
  
  // Handle download
  const handleDownload = async () => {
    if (treeContainerRef.current) {
      try {
        const canvas = await html2canvas(treeContainerRef.current, {
          backgroundColor: null,
          scale: 2
        });
        
        const link = document.createElement('a');
        link.download = `${videoTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_mindmap.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        
        onDownload();
      } catch (error) {
        console.error('Error generating image:', error);
      }
    }
    
    handleMenuClose();
  };
  
  // Handle print
  const handlePrint = () => {
    if (treeContainerRef.current) {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Mind Map: ${videoTitle}</title>
              <style>
                body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
                h1 { color: #333; font-size: 24px; margin-bottom: 20px; }
              </style>
            </head>
            <body>
              <h1>Mind Map: ${videoTitle}</h1>
              ${treeContainerRef.current.outerHTML}
              <script>
                window.onload = function() { window.print(); window.close(); }
              </script>
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    }
    
    onPrint();
    handleMenuClose();
  };
  
  // Handle zoom in
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 2));
  };
  
  // Handle zoom out
  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
  };
  
  // Handle zoom change
  const handleZoomChange = (_event: Event, newValue: number | number[]) => {
    setZoomLevel(newValue as number);
  };
  
  // Handle fullscreen toggle
  const handleFullscreenToggle = () => {
    setIsFullscreen(prev => !prev);
  };
  
  // Handle node click
  const handleNodeClick = (nodeData: any) => {
    setSelectedNode(nodeData.data);
  };
  
  // Handle node info toggle
  const handleNodeInfoToggle = () => {
    setShowNodeInfo(prev => !prev);
  };
  
  // Handle color mode toggle
  const handleColorModeToggle = () => {
    setColorMode(prev => !prev);
  };
  
  // Handle center view
  const handleCenterView = () => {
    if (Tree && treeContainerRef.current) {
      // This is a placeholder - in a real implementation, you would use the Tree API to center the view
      setZoomLevel(1);
    }
  };
  
  // Custom node component
  const renderCustomNode = ({ nodeDatum, toggleNode }: any) => {
    const nodeColor = colorMode && nodeDatum.attributes.color 
      ? nodeDatum.attributes.color 
      : theme.palette.primary.main;
    
    const isSelected = selectedNode && selectedNode.name === nodeDatum.name;
    const hasChildren = nodeDatum.children && nodeDatum.children.length > 0;
    
    return (
      <g onClick={() => handleNodeClick(nodeDatum)}>
        <circle 
          r={isSelected ? 25 : 20} 
          fill={alpha(nodeColor, isSelected ? 0.9 : 0.7)}
          stroke={nodeColor}
          strokeWidth={isSelected ? 3 : 2}
          onClick={toggleNode}
          style={{ cursor: 'pointer', transition: 'all 0.3s ease' }}
        />
        
        {hasChildren && (
          <circle
            r={8}
            cx={isSelected ? 25 : 20}
            cy={0}
            fill={theme.palette.background.paper}
            stroke={nodeColor}
            strokeWidth={1}
            onClick={toggleNode}
            style={{ cursor: 'pointer' }}
          />
        )}
        
        {hasChildren && (
          <text
            fill={nodeColor}
            x={isSelected ? 25 : 20}
            y={0}
            textAnchor="middle"
            dy=".3em"
            fontSize={10}
            fontWeight="bold"
            onClick={toggleNode}
            style={{ cursor: 'pointer', pointerEvents: 'none' }}
          >
            {nodeDatum.__rd3t.collapsed ? '+' : '-'}
          </text>
        )}
        
        <text 
          fill="white" 
          strokeWidth="0.5"
          stroke="white"
          fontWeight="bold"
          fontSize={12}
          textAnchor="middle"
          dy=".3em"
          style={{ pointerEvents: 'none' }}
        >
          {nodeDatum.name.substring(0, 2)}
        </text>
        
        <text 
          fill={theme.palette.text.primary}
          x={isSelected ? 35 : 30}
          dy=".3em"
          fontSize={14}
          fontWeight={isSelected ? 600 : 500}
          style={{ pointerEvents: 'none' }}
        >
          {nodeDatum.name}
        </text>
      </g>
    );
  };
  
  // Effect to handle fullscreen
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    
    document.addEventListener('keydown', handleEscKey);
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isFullscreen]);
  
  return (
    <Paper 
      elevation={0} 
      className="glass-container"
      sx={{ 
        p: 3,
        mb: 4,
        position: isFullscreen ? 'fixed' : 'relative',
        top: isFullscreen ? 0 : 'auto',
        left: isFullscreen ? 0 : 'auto',
        right: isFullscreen ? 0 : 'auto',
        bottom: isFullscreen ? 0 : 'auto',
        zIndex: isFullscreen ? 1300 : 'auto',
        width: isFullscreen ? '100vw' : '100%',
        height: isFullscreen ? '100vh' : 600,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h2">
          Mind Map
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TimeSavedIndicator minutes={timeSaved} />
          
          <Box sx={{ display: 'flex', ml: 2 }}>
            <Tooltip title={isSaved ? "Saved to your library" : "Save to your library"}>
              <IconButton onClick={handleSave} color={isSaved ? "primary" : "default"}>
                {isSaved ? <BookmarkIcon /> : <BookmarkBorderIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Toggle node info">
              <IconButton onClick={handleNodeInfoToggle} color={showNodeInfo ? "primary" : "default"}>
                {showNodeInfo ? <InfoIcon /> : <InfoOutlinedIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Toggle color mode">
              <IconButton onClick={handleColorModeToggle} color={colorMode ? "primary" : "default"}>
                <FormatColorFillIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Center view">
              <IconButton onClick={handleCenterView}>
                <CenterFocusStrongIcon />
              </IconButton>
            </Tooltip>
            
            <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
              <IconButton onClick={handleFullscreenToggle}>
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
            
            <Tooltip title="More options">
              <IconButton
                onClick={handleMoreMenuOpen}
                aria-label="more options"
                aria-controls="more-menu"
                aria-haspopup="true"
              >
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
      
      <Divider sx={{ mb: 2 }} />
      
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Tooltip title="Zoom out">
          <IconButton onClick={handleZoomOut} disabled={zoomLevel <= 0.5}>
            <ZoomOutIcon />
          </IconButton>
        </Tooltip>
        
        <Slider
          value={zoomLevel}
          min={0.5}
          max={2}
          step={0.1}
          onChange={handleZoomChange}
          aria-labelledby="zoom-slider"
          sx={{ mx: 2, width: 100 }}
        />
        
        <Tooltip title="Zoom in">
          <IconButton onClick={handleZoomIn} disabled={zoomLevel >= 2}>
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
        
        <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
          {Math.round(zoomLevel * 100)}%
        </Typography>
      </Box>
      
      <Box 
        ref={containerRef}
        sx={{ 
          flexGrow: 1, 
          position: 'relative',
          border: '1px solid rgba(255, 255, 255, 0.05)',
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: 'rgba(18, 18, 18, 0.5)'
        }}
      >
        <div 
          ref={treeContainerRef}
          style={{ 
            width: '100%', 
            height: '100%',
            position: 'relative'
          }}
        >
          {!Tree ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : treeData ? (
            <Tree
              data={treeData}
              orientation="horizontal"
              pathFunc="step"
              translate={{ x: dimensions.width / 5, y: dimensions.height / 2 }}
              zoom={zoomLevel}
              nodeSize={{ x: 200, y: 100 }}
              separation={{ siblings: 1, nonSiblings: 1.5 }}
              renderCustomNodeElement={renderCustomNode}
              onNodeClick={handleNodeClick}
              enableLegacyTransitions
              transitionDuration={800}
              collapsible={true}
              zoomable={true}
              draggable={true}
              scaleExtent={{ min: 0.5, max: 2 }}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography variant="body1" color="text.secondary">
                No mind map data available
              </Typography>
            </Box>
          )}
        </div>
      </Box>
      
      <Collapse in={showNodeInfo && !!selectedNode}>
        <Paper 
          elevation={0}
          sx={{ 
            mt: 2,
            p: 2,
            backgroundColor: alpha(selectedNode?.attributes.color || theme.palette.primary.main, 0.05),
            border: `1px solid ${alpha(selectedNode?.attributes.color || theme.palette.primary.main, 0.1)}`,
            borderRadius: 2
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {selectedNode?.name}
          </Typography>
          
          {selectedNode?.attributes.details ? (
            <Typography variant="body2">
              {selectedNode.attributes.details}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              No additional details available for this node.
            </Typography>
          )}
        </Paper>
      </Collapse>
      
      {/* More options menu */}
      <Menu
        id="more-menu"
        anchorEl={moreAnchorEl}
        open={moreMenuOpen}
        onClose={handleMenuClose}
        MenuListProps={{
          'aria-labelledby': 'more-button',
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleShare}>
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownload}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download as Image</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePrint}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print</ListItemText>
        </MenuItem>
      </Menu>
    </Paper>
  );
};

export default MindMapEnhanced;
